import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { generatedApi, MissionControlTenant } from "src/store/api/generatedApi";
import { useIsAdmin } from "hooks/settings";
import { MillMap } from "components/millMap/MillMap";
import {
  MissionControlTab,
  TenantListPanel,
} from "components/millMap/TenantListPanel";
import { MissionControlUploadDialog } from "components/millMap/MissionControlUploadDialog";
import { PlannedShredPanel } from "components/millMap/PlannedShredPanel";

export const SelectTenantPage = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { tenantGroup } = useParams<{ tenantGroup: string }>();
  const isAdmin = useIsAdmin();
  const { data: missionControlTenantsResponse, isLoading } =
    generatedApi.endpoints.listMissionControlTenants.useQuery({
      tenantGroupName: tenantGroup,
      visibility: isAdmin ? "all" : "user",
    });
  const [sendNotification] =
    generatedApi.useSendMissionControlNotificationMutation();

  useEffect(() => {
    void sendNotification({ tenantGroupName: tenantGroup });
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- mount-only: send notification once when tenant group page loads
  }, []);

  const [selectedTenant, setSelectedTenant] =
    useState<MissionControlTenant | null>(null);
  const [hoveredTenant, setHoveredTenant] =
    useState<MissionControlTenant | null>(null);
  const [activeTab, setActiveTab] = useState<MissionControlTab>("savings");
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);

  const tenants = useMemo(
    () => missionControlTenantsResponse?.tenants ?? [],
    [missionControlTenantsResponse?.tenants]
  );
  const savingsTarget = missionControlTenantsResponse?.savings_target ?? 0;

  const tenantsWithCoords = useMemo(
    () =>
      tenants.filter(
        (t) => Number.isFinite(t.longitude) && Number.isFinite(t.latitude)
      ),
    [tenants]
  );

  return (
    <Box
      sx={{
        position: "relative",
        backgroundColor: theme.palette.background.default,
        flex: 1,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          p: 4,
          pb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <Stack direction="row" alignItems="baseline" gap={1.5}>
          <Typography variant="h4" component="h1" fontWeight="bold">
            {t("selectMill")}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t("selectAMillToAccessTheSystem")}
          </Typography>
        </Stack>
        <Button
          variant="contained"
          startIcon={<CloudUploadIcon />}
          onClick={() => {
            setUploadDialogOpen(true);
          }}
        >
          {t("uploadSavingsData")}
        </Button>
      </Box>

      <MissionControlUploadDialog
        open={uploadDialogOpen}
        onClose={() => {
          setUploadDialogOpen(false);
        }}
      />

      {isLoading ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexGrow: 1,
          }}
        >
          <CircularProgress size={48} />
        </Box>
      ) : (
        <Stack
          direction="row"
          gap={2}
          sx={{ flexGrow: 1, minHeight: 0, px: 2, pb: 1 }}
        >
          <Stack sx={{ flexGrow: 1, minWidth: 0, gap: 1 }}>
            <MillMap
              tenants={tenantsWithCoords}
              selectedTenant={selectedTenant}
              onSelectTenant={setSelectedTenant}
              hoveredTenant={hoveredTenant}
              activeTab={activeTab}
              onTabChange={setActiveTab}
            />
            <Box sx={{ flexShrink: 0 }}>
              <PlannedShredPanel tenants={tenants} />
            </Box>
          </Stack>
          <TenantListPanel
            tenants={tenants}
            savingsTarget={savingsTarget}
            selectedTenant={selectedTenant}
            onSelectTenant={setSelectedTenant}
            onHoverTenant={setHoveredTenant}
            activeTab={activeTab}
            onTabChange={setActiveTab}
          />
        </Stack>
      )}
    </Box>
  );
};
